import { useCallback, useEffect, useMemo, useState } from "react";

import { AppShell } from "../ui/AppShell";
import { PageHead } from "../ui/PageHead";
import { blockStyle } from "../ui/Block";
import { ApiError } from "../lib/auth";
import {
  createUser,
  createResetLink,
  formatDay,
  listOrgs,
  listUsers,
  setUserAdmin,
  setUserOrg,
  setUserPassword,
  setUserStatus,
  type OrgSummary,
  type PlatformUser,
} from "../lib/usersApi";
import { setUserReportLimit } from "../lib/usageApi";
import { refreshUsage } from "../lib/useUsage";
import { AdminSourceDiagnostics } from "./AdminSourceDiagnostics";
import "./users.css";

/**
 * `/users` — Admin → Users.
 *
 * WHAT THIS SCREEN IS FOR. Until it existed there was no way to create a user: no admin screen,
 * no invite endpoint, and a `POST /api/auth/signup` the client never called. Allow-listing an
 * address in `VALUBRIEF_ALLOWED_EMAILS` accomplished nothing on its own, because even a permitted
 * address had no page on which to sign up. This is that page.
 *
 * THE FLOW IT IS BUILT AROUND, and the reason the "Set a password now" option is the second one:
 * an admin adds a person WITHOUT choosing a password and is handed a one-time reset link. They
 * pass it on however they like — chat, phone, a different email account. That person opens it and
 * sets their own password. The admin never learns it, which is the only version of this where
 * nobody ends up knowing a credential that is not theirs.
 *
 * THE ONE-TIME LINK IS TREATED AS ONE. It arrives in the response that mints it, it is shown
 * once, and closing the dialog drops it from component state. It is not in the database (only its
 * sha256 is), not in a log, and not in browser storage — nothing on this screen ever touches
 * `localStorage`, per the project rule and `lib/auth.ts`'s. If it is lost, mint another; there is
 * no way to recover it, and that is the design rather than a limitation.
 *
 * AUTHORIZATION IS THE SERVER'S. This screen renders only for an admin and the nav entry is
 * hidden otherwise, but that is convenience. Every route it calls re-checks `require_admin` on
 * every request, and a non-admin who types the URL gets a screen full of 403s rather than data.
 *
 * UNKNOWN IS NEVER ZERO. `last_seen_at: null` renders as "— never signed in", with the reason
 * attached, never as a blank cell and never as a date. So does an account with no organization
 * yet. The rule the reports live by applies to the admin screen too.
 */

type Dialog =
  | { kind: "add" }
  | { kind: "password"; user: PlatformUser }
  | { kind: "link"; user: PlatformUser; url: string; expires: string | null }
  | { kind: "limit"; user: PlatformUser }
  | null;

const ROLES = ["Broker", "Appraiser", "Tax consultant", "Attorney"];

const DEFAULT_MONTHLY_LIMIT = 10;

/** §6, board 3: "(default)" or "set by …, date" — never the bare number alone, because the
 * number without its provenance is exactly the ambiguity this column exists to remove (a 10
 * nobody touched reads identically to a 10 an admin just set back down from 25). */
function MonthlyLimitCell({ user: p }: { user: PlatformUser }) {
  if (p.is_admin) {
    return <span style={{ color: "var(--vb-color-text-muted, #5B6B7F)" }}>Not limited (admin)</span>;
  }
  const n = p.monthly_report_limit ?? DEFAULT_MONTHLY_LIMIT;
  if (p.monthly_report_limit == null) {
    return (
      <>
        {n} <span style={{ fontSize: 12, color: "var(--vb-color-text-muted, #5B6B7F)" }}>(default)</span>
      </>
    );
  }
  const who = p.monthly_limit_set_by_name || p.monthly_limit_set_by || "an admin";
  const when = formatDay(p.monthly_limit_set_at);
  return (
    <>
      <b>{n}</b>{" "}
      <span style={{ fontSize: 12, color: "var(--vb-color-text-muted, #5B6B7F)" }}>
        set by {who}{when ? `, ${when}` : ""}
      </span>
    </>
  );
}

function Chip({ tone, children }: { tone?: "good" | "warn" | "accent"; children: React.ReactNode }) {
  return (
    <span className="vb-chip" data-tone={tone}>
      {children}
    </span>
  );
}

/** An em dash and the reason it is an em dash. Never one without the other. */
function Unknown({ reason }: { reason: string }) {
  return <span className="vb-users-unknown">— {reason}</span>;
}

export function UsersScreen() {
  const [users, setUsers] = useState<PlatformUser[] | null>(null);
  const [mailConfigured, setMailConfigured] = useState(false);
  const [orgs, setOrgs] = useState<OrgSummary[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState("");
  const [openRow, setOpenRow] = useState<string | null>(null);
  const [dialog, setDialog] = useState<Dialog>(null);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const [list, orgList] = await Promise.all([listUsers(), listOrgs()]);
      setUsers(list.users);
      setMailConfigured(list.mail_configured);
      setOrgs(orgList);
      setError(null);
    } catch (e) {
      setUsers([]);
      setError(
        e instanceof ApiError && e.status === 403
          ? "This session does not hold admin access. If you are an admin, sign in again to complete the second factor."
          : "The list of accounts could not be loaded.",
      );
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const shown = useMemo(() => {
    const t = filter.trim().toLowerCase();
    if (!users) return [];
    if (!t) return users;
    return users.filter(
      (p) =>
        p.name.toLowerCase().includes(t) ||
        p.email.toLowerCase().includes(t) ||
        (p.org_name || "").toLowerCase().includes(t),
    );
  }, [users, filter]);

  /** One place every mutation goes through, so one place surfaces the server's own words.
   *  A 409 here is not a bug — it is the server refusing to let the last admin be removed, and
   *  its message names the reason, so it is shown verbatim rather than rewritten. */
  async function run(what: () => Promise<unknown>, said: string) {
    setBusy(true);
    setNote(null);
    setError(null);
    try {
      await what();
      await refresh();
      setNote(said);
    } catch (e) {
      setError(e instanceof ApiError ? String(e.message) : "That did not work.");
    } finally {
      setBusy(false);
    }
  }

  const neverIn = (users || []).filter((p) => !p.last_seen_at).length;
  const admins = (users || []).filter((p) => p.is_admin).length;
  const active = (users || []).filter((p) => p.status === "active").length;

  return (
    <AppShell step="users" content="form">
      <div style={{ ...blockStyle("form"), padding: "8px 0 48px" }}>
        <PageHead
          eyebrow="Admin"
          title="Users"
          sub={
            <>
              Everyone who can sign in to this ValuBrief service. Accounts are created here — there
              is no public sign-up while the pilot is closed.
            </>
          }
          actions={
            <button
              className="vb-users-btn"
              data-variant="primary"
              type="button"
              onClick={() => setDialog({ kind: "add" })}
            >
              Add a user
            </button>
          }
        />

        <p className="vb-users-notice" style={{ marginTop: 18 }}>
          <span>
            {mailConfigured ? (
              <>
                <strong>Reset links are emailed, and can also be handed over.</strong> Email is
                configured on this service, so a user who asks for a reset receives one. You can
                still issue a link yourself from the row below when somebody cannot reach their
                inbox.
              </>
            ) : (
              <>
                <strong>Email is not configured on this service.</strong> ValuBrief cannot send an
                invitation or a reset link, so you issue the link here and pass it to them
                yourself. Set <code>VALUBRIEF_SMTP_*</code> and this notice changes.
              </>
            )}
          </span>
        </p>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: "8px 24px",
            margin: "18px 0 10px",
            fontSize: 13,
          }}
        >
          <span>
            <strong>{users?.length ?? "—"}</strong> accounts
          </span>
          <span>
            <strong>{active}</strong> active
          </span>
          <span>
            <strong>{admins}</strong> with admin access
          </span>
          <span>
            <strong>{neverIn}</strong> have never signed in
          </span>
          <input
            aria-label="Filter users"
            placeholder="Filter by name, email or organization"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            style={{
              marginLeft: "auto",
              font: "inherit",
              fontSize: 13,
              padding: "6px 10px",
              borderRadius: 8,
              border: "1px solid var(--vb-color-border, #E7ECF2)",
              background: "var(--vb-color-surface, #FFFFFF)",
              color: "var(--vb-color-text, #1E293B)",
              minWidth: 240,
            }}
          />
        </div>

        {note && (
          <p role="status" style={{ fontSize: 13, margin: "0 0 10px" }}>
            {note}
          </p>
        )}
        {error && (
          <p className="vb-users-error" role="alert" style={{ marginBottom: 10 }}>
            {error}
          </p>
        )}

        <div
          style={{
            overflowX: "auto",
            border: "1px solid var(--vb-color-border, #E7ECF2)",
            borderRadius: 12,
            background: "var(--vb-color-surface, #FFFFFF)",
          }}
        >
          <table className="vb-users-table">
            <thead>
              <tr>
                <th scope="col">User</th>
                <th scope="col">Role</th>
                <th scope="col">Organization</th>
                <th scope="col">Status</th>
                <th scope="col">Last signed in</th>
                <th scope="col">Password</th>
                <th scope="col">Monthly limit</th>
                <th scope="col" />
              </tr>
            </thead>
            <tbody>
              {users === null && (
                <tr>
                  <td colSpan={8} style={{ padding: 28, textAlign: "center" }}>
                    Loading accounts…
                  </td>
                </tr>
              )}
              {users !== null && shown.length === 0 && (
                <tr>
                  <td colSpan={8} style={{ padding: 28, textAlign: "center" }}>
                    {filter ? `No account matches “${filter}”.` : "No accounts yet."}
                  </td>
                </tr>
              )}
              {shown.map((p) => (
                <UserRows
                  key={p.id}
                  user={p}
                  orgs={orgs}
                  busy={busy}
                  open={openRow === p.id}
                  onToggle={() => setOpenRow(openRow === p.id ? null : p.id)}
                  onResetLink={async () => {
                    setBusy(true);
                    setError(null);
                    setNote(null);
                    try {
                      const r = await createResetLink(p.id);
                      setDialog({ kind: "link", user: p, url: r.url, expires: r.expires_at });
                    } catch (e) {
                      setError(e instanceof ApiError ? String(e.message) : "That did not work.");
                    } finally {
                      setBusy(false);
                    }
                  }}
                  onSetPassword={() => setDialog({ kind: "password", user: p })}
                  onToggleStatus={() =>
                    run(
                      () =>
                        setUserStatus(p.id, p.status === "active" ? "disabled" : "active"),
                      p.status === "active"
                        ? `${p.email} is disabled. Their access ended on their next request, not at their next sign-in.`
                        : `${p.email} is active again. They can sign in with their existing password.`,
                    )
                  }
                  onToggleAdmin={() =>
                    run(
                      () => setUserAdmin(p.id, !p.is_admin),
                      p.is_admin
                        ? `${p.email} no longer has admin access, and their sessions were ended.`
                        : `${p.email} now has admin access.`,
                    )
                  }
                  onMoveOrg={(choice) =>
                    run(() => setUserOrg(p.id, choice), `${p.email} moved organization.`)
                  }
                  onChangeLimit={() => setDialog({ kind: "limit", user: p })}
                />
              ))}
            </tbody>
          </table>
        </div>
        {/* Renders nothing unless the server says this admin is on the diagnostics allow-list. */}
        <AdminSourceDiagnostics />
      </div>

      {dialog?.kind === "add" && (
        <AddUserDialog
          orgs={orgs}
          onClose={() => setDialog(null)}
          onCreated={async (person, url, expires) => {
            await refresh();
            if (url) setDialog({ kind: "link", user: person, url, expires });
            else {
              setDialog(null);
              setNote(`Created ${person.email}. They can sign in with the password you chose.`);
            }
          }}
        />
      )}

      {dialog?.kind === "password" && (
        <SetPasswordDialog
          user={dialog.user}
          onClose={() => setDialog(null)}
          onDone={(revoked) => {
            setDialog(null);
            setNote(
              `Password replaced for ${dialog.user.email}. ${revoked} session${revoked === 1 ? "" : "s"} ended.`,
            );
            void refresh();
          }}
        />
      )}

      {dialog?.kind === "link" && (
        <ResetLinkDialog
          user={dialog.user}
          url={dialog.url}
          expires={dialog.expires}
          // Closing DROPS the link. It is not kept in state, not stashed anywhere, and cannot be
          // recovered — mint another instead. Anything else would quietly turn a one-time secret
          // into one that lives as long as the tab does.
          onClose={() => setDialog(null)}
        />
      )}

      {dialog?.kind === "limit" && (
        <ReportLimitDialog
          user={dialog.user}
          onClose={() => setDialog(null)}
          onSaved={(limit) => {
            setDialog(null);
            setNote(
              limit === null
                ? `${dialog.user.email} is back on the default of ${DEFAULT_MONTHLY_LIMIT} reports a month.`
                : `${dialog.user.email}'s monthly limit is now ${limit}.`,
            );
            void refresh();
            // §6 (gap closed 2026-10-02): this admin's own pill/tile/line, and — on this agent's
            // next page load — theirs too, must reflect the new limit rather than a cached one.
            refreshUsage();
          }}
        />
      )}
    </AppShell>
  );
}

/* -------------------------------------------------------------------------- */

function UserRows({
  user: p,
  orgs,
  open,
  busy,
  onToggle,
  onResetLink,
  onSetPassword,
  onToggleStatus,
  onToggleAdmin,
  onMoveOrg,
  onChangeLimit,
}: {
  user: PlatformUser;
  orgs: OrgSummary[];
  open: boolean;
  busy: boolean;
  onToggle: () => void;
  onResetLink: () => void;
  onSetPassword: () => void;
  onToggleStatus: () => void;
  onToggleAdmin: () => void;
  onMoveOrg: (choice: { org_id?: string; org_name?: string }) => void;
  onChangeLimit: () => void;
}) {
  const [moveTo, setMoveTo] = useState("");
  return (
    <>
      <tr data-status={p.status}>
        <td>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <span className="vb-users-name">{p.name}</span>
            {p.is_admin && <Chip tone="accent">Admin</Chip>}
          </div>
          <div className="vb-users-email">{p.email}</div>
        </td>
        <td>{p.role}</td>
        <td>{p.org_name || <Unknown reason="no organization" />}</td>
        <td>
          {p.status === "active" ? <Chip tone="good">Active</Chip> : <Chip tone="warn">Disabled</Chip>}
        </td>
        <td>{formatDay(p.last_seen_at) || <Unknown reason="never signed in" />}</td>
        <td>
          {p.has_password ? (
            <span style={{ fontSize: 12 }}>Set by them</span>
          ) : (
            <Chip tone="warn">Waiting on a reset link</Chip>
          )}
        </td>
        {/* §6, board 3: "(default)" / "set by …, date" / "Not limited (admin)" — never the bare
            number, which would read the same whether nobody ever touched it or an admin set it
            back down from a higher figure. */}
        <td>
          <MonthlyLimitCell user={p} />
        </td>
        <td style={{ textAlign: "right" }}>
          <button className="vb-users-btn" type="button" onClick={onToggle} aria-expanded={open}>
            {open ? "Close" : "Manage"}
          </button>
        </td>
      </tr>
      {open && (
        <tr className="vb-users-drawer">
          <td colSpan={8}>
            <dl>
              <div>
                <dt>User ID</dt>
                <dd>{p.id}</dd>
              </div>
              <div>
                <dt>Created</dt>
                <dd>{formatDay(p.created_at) || <Unknown reason="not recorded" />}</dd>
              </div>
              <div>
                <dt>Organization</dt>
                <dd>{p.org_name || <Unknown reason="none" />}</dd>
              </div>
            </dl>
            <div className="vb-users-actions">
              <button className="vb-users-btn" type="button" disabled={busy} onClick={onResetLink}>
                Issue a reset link
              </button>
              <button className="vb-users-btn" type="button" disabled={busy} onClick={onSetPassword}>
                Set a password for them
              </button>
              <button className="vb-users-btn" type="button" disabled={busy} onClick={onToggleAdmin}>
                {p.is_admin ? "Revoke admin access" : "Grant admin access"}
              </button>
              {/* §6: admins are never limited, so there is nothing here to change — the same rule
                  `MonthlyLimitCell` applies to the column. */}
              {!p.is_admin && (
                <button className="vb-users-btn" type="button" disabled={busy} onClick={onChangeLimit}>
                  Change monthly limit
                </button>
              )}
              <button
                className="vb-users-btn"
                data-variant="caution"
                type="button"
                disabled={busy}
                onClick={onToggleStatus}
              >
                {p.status === "active" ? "Disable account" : "Re-enable account"}
              </button>
            </div>
            <div style={{ display: "flex", gap: 8, alignItems: "center", marginTop: 12 }}>
              <div className="vb-users-field" style={{ flex: "0 1 320px" }}>
                <label htmlFor={`vb-move-${p.id}`}>Move to organization</label>
                <select id={`vb-move-${p.id}`} value={moveTo}
                        onChange={(e) => setMoveTo(e.target.value)}>
                  <option value="">Choose an organization…</option>
                  {orgs.map((o) => (
                    <option key={o.id} value={o.id}>
                      {o.name} ({o.members})
                    </option>
                  ))}
                </select>
              </div>
              <button
                className="vb-users-btn"
                type="button"
                disabled={busy || !moveTo}
                style={{ alignSelf: "flex-end" }}
                onClick={() => onMoveOrg({ org_id: moveTo })}
              >
                Move
              </button>
            </div>
          </td>
        </tr>
      )}
    </>
  );
}

/* -------------------------------------------------------------------------- */

function Dialog({
  title,
  sub,
  onClose,
  children,
  footer,
}: {
  title: string;
  sub?: React.ReactNode;
  onClose: () => void;
  children: React.ReactNode;
  footer: React.ReactNode;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div className="vb-users-scrim">
      <div className="vb-users-dialog" role="dialog" aria-modal="true" aria-label={title}>
        <div className="vb-users-dialog-head">
          <h2>{title}</h2>
          {sub && (
            <p style={{ margin: "4px 0 0", fontSize: 12, lineHeight: 1.55, color: "var(--vb-color-text-muted, #5B6B7F)" }}>
              {sub}
            </p>
          )}
        </div>
        <div className="vb-users-dialog-body">{children}</div>
        <div className="vb-users-dialog-foot">{footer}</div>
      </div>
    </div>
  );
}

function AddUserDialog({
  orgs,
  onClose,
  onCreated,
}: {
  orgs: OrgSummary[];
  onClose: () => void;
  onCreated: (user: PlatformUser, url: string | null, expires: string | null) => void;
}) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [role, setRole] = useState(ROLES[0]);
  const [orgMode, setOrgMode] = useState<"own" | "existing" | "new">("own");
  const [orgId, setOrgId] = useState("");
  const [orgName, setOrgName] = useState("");
  /* "Send a link" first and selected by default. The alternative means the admin knows a password
     that is not theirs, which is worth a deliberate click. */
  const [mode, setMode] = useState<"link" | "password">("link");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const emailLooksReal = /.+@.+\..+/.test(email.trim());
  const orgOk = orgMode !== "existing" || orgId !== "";
  const orgNamed = orgMode !== "new" || orgName.trim() !== "";
  const ok =
    !busy && name.trim() !== "" && emailLooksReal && orgOk && orgNamed &&
    (mode === "link" || password.length >= 12);

  async function submit() {
    setBusy(true);
    setError(null);
    try {
      const r = await createUser({
        name: name.trim(),
        email: email.trim().toLowerCase(),
        role,
        ...(mode === "password" ? { password } : {}),
        ...(orgMode === "existing" ? { org_id: orgId } : {}),
        ...(orgMode === "new" ? { org_name: orgName.trim() } : {}),
      });
      onCreated(r.user, r.reset_url, r.expires_at);
    } catch (e) {
      setError(e instanceof ApiError ? String(e.message) : "The account could not be created.");
      setBusy(false);
    }
  }

  return (
    <Dialog
      title="Add a user"
      sub="Creates the account immediately."
      onClose={onClose}
      footer={
        <>
          <button className="vb-users-btn" type="button" onClick={onClose}>
            Cancel
          </button>
          <button className="vb-users-btn" data-variant="primary" type="button" disabled={!ok} onClick={submit}>
            {busy ? "Creating…" : "Create account"}
          </button>
        </>
      }
    >
      <div className="vb-users-field">
        <label htmlFor="vb-add-name">Full name</label>
        <input id="vb-add-name" value={name} onChange={(e) => setName(e.target.value)}
               placeholder="Richard Fricks" />
      </div>
      <div className="vb-users-field">
        <label htmlFor="vb-add-email">Email</label>
        <input
          id="vb-add-email"
          type="email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          placeholder="rsfricks@yahoo.com"
          aria-describedby="vb-add-email-hint"
        />
        <span className="vb-users-hint" id="vb-add-email-hint">
          This is the sign-in identity. It does not have to be a valubrief.ai address.
        </span>
      </div>
      <div className="vb-users-field">
        <label htmlFor="vb-add-role">Role</label>
        <select id="vb-add-role" value={role} onChange={(e) => setRole(e.target.value)}>
          {ROLES.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
      </div>

      <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
        <legend className="vb-users-field" style={{ padding: 0 }}>
          Organization
        </legend>
        <label style={{ display: "block", fontSize: 13, marginTop: 6 }}>
          <input
            type="radio"
            checked={orgMode === "own"}
            onChange={() => setOrgMode("own")}
          />{" "}
          Their own, named after them
        </label>
        <label style={{ display: "block", fontSize: 13, marginTop: 4 }}>
          <input
            type="radio"
            checked={orgMode === "existing"}
            onChange={() => setOrgMode("existing")}
            disabled={orgs.length === 0}
          />{" "}
          An existing organization
        </label>
        {orgMode === "existing" && (
          <select value={orgId} onChange={(e) => setOrgId(e.target.value)} style={{ marginTop: 6 }}>
            <option value="">Choose one…</option>
            {orgs.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name} ({o.members})
              </option>
            ))}
          </select>
        )}
        <label style={{ display: "block", fontSize: 13, marginTop: 4 }}>
          <input type="radio" checked={orgMode === "new"} onChange={() => setOrgMode("new")} /> A new
          organization
        </label>
        {orgMode === "new" && (
          <input
            value={orgName}
            onChange={(e) => setOrgName(e.target.value)}
            placeholder="Realty Firm Global"
            style={{ marginTop: 6 }}
          />
        )}
        <span className="vb-users-hint">
          Recording who somebody belongs to. It does not change what anyone can see today.
        </span>
      </fieldset>

      <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
        <legend className="vb-users-field" style={{ padding: 0 }}>
          First password
        </legend>
        <label style={{ display: "block", fontSize: 13, marginTop: 6 }}>
          <input type="radio" checked={mode === "link"} onChange={() => setMode("link")} /> Send them
          a reset link and let them choose it
        </label>
        <label style={{ display: "block", fontSize: 13, marginTop: 4 }}>
          <input type="radio" checked={mode === "password"} onChange={() => setMode("password")} />{" "}
          Set a password now
        </label>
        {mode === "password" ? (
          <>
            <input
              type="text"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 12 characters"
              style={{ marginTop: 6 }}
            />
            <span className="vb-users-hint">
              You will know this user’s password, and on this service there is no way for them to
              change it except through a link you issue. Prefer the option above.
            </span>
          </>
        ) : (
          <span className="vb-users-hint">
            The account is created with no usable password, and a one-time link comes back on the
            next screen. You never learn what they choose.
          </span>
        )}
      </fieldset>

      {error && (
        <p className="vb-users-error" role="alert">
          {error}
        </p>
      )}
    </Dialog>
  );
}

function SetPasswordDialog({
  user: person,
  onClose,
  onDone,
}: {
  user: PlatformUser;
  onClose: () => void;
  onDone: (revoked: number) => void;
}) {
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  return (
    <Dialog
      title={`Set a password for ${person.name}`}
      sub="This replaces their current password and signs them out everywhere."
      onClose={onClose}
      footer={
        <>
          <button className="vb-users-btn" type="button" onClick={onClose}>
            Cancel
          </button>
          <button
            className="vb-users-btn"
            data-variant="primary"
            type="button"
            disabled={busy || password.length < 12}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                const r = await setUserPassword(person.id, password);
                onDone(r.sessions_revoked);
              } catch (e) {
                setError(e instanceof ApiError ? String(e.message) : "That did not work.");
                setBusy(false);
              }
            }}
          >
            {busy ? "Setting…" : "Set password"}
          </button>
        </>
      }
    >
      <div className="vb-users-field">
        <label htmlFor="vb-set-password">New password</label>
        <input
          id="vb-set-password"
          type="text"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          placeholder="At least 12 characters"
          aria-describedby="vb-set-password-hint"
        />
        <span className="vb-users-hint" id="vb-set-password-hint">
          Give it to them directly — nothing is emailed. Issuing a reset link instead means nobody
          but them ever knows it.
        </span>
      </div>
      {error && (
        <p className="vb-users-error" role="alert">
          {error}
        </p>
      )}
    </Dialog>
  );
}

/**
 * §6, board 3 — "Change monthly limit": the number, the reason (required, kept in the history)
 * and an after-save preview. Modeled on `SetPasswordDialog` above, the existing shape this screen
 * already uses for "ask for one new value, state what changes, save or cancel."
 *
 * A BLANK NUMBER IS A REAL CHOICE, NOT AN EMPTY FIELD. Clearing the box means "go back to the
 * default of 10" — `setUserReportLimit` sends `limit: null` for that, never `0`: the owner's own
 * rule is that unknown is never zero, and a blank box is not the same claim as "zero reports a
 * month."
 */
function ReportLimitDialog({
  user: person,
  onClose,
  onSaved,
}: {
  user: PlatformUser;
  onClose: () => void;
  onSaved: (limit: number | null) => void;
}) {
  const [value, setValue] = useState(
    person.monthly_report_limit == null ? "" : String(person.monthly_report_limit),
  );
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const trimmed = value.trim();
  const parsed = trimmed === "" ? null : Number(trimmed);
  const validNumber = trimmed === "" || (Number.isInteger(parsed) && (parsed as number) > 0);
  const canSave = validNumber && reason.trim().length > 0 && !busy;

  const previewLimit = parsed ?? DEFAULT_MONTHLY_LIMIT;

  return (
    <Dialog
      title={`Change monthly limit for ${person.name}`}
      sub={`${person.monthly_report_limit ?? DEFAULT_MONTHLY_LIMIT} per month today${
        person.monthly_report_limit == null ? " (the default)" : ""
      }.`}
      onClose={onClose}
      footer={
        <>
          <button className="vb-users-btn" type="button" onClick={onClose}>
            Cancel
          </button>
          <button
            className="vb-users-btn"
            data-variant="primary"
            type="button"
            disabled={!canSave}
            onClick={async () => {
              setBusy(true);
              setError(null);
              try {
                await setUserReportLimit(person.id, { limit: parsed, reason: reason.trim() });
                onSaved(parsed);
              } catch (e) {
                setError(e instanceof ApiError ? String(e.message) : "That did not work.");
                setBusy(false);
              }
            }}
          >
            {busy ? "Saving…" : "Save limit"}
          </button>
        </>
      }
    >
      <div className="vb-users-field">
        <label htmlFor="vb-limit-number">Reports per month</label>
        <input
          id="vb-limit-number"
          type="number"
          min={1}
          step={1}
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={String(DEFAULT_MONTHLY_LIMIT)}
          style={{ maxWidth: 140 }}
          aria-describedby="vb-limit-number-hint"
        />
        <span className="vb-users-hint" id="vb-limit-number-hint">
          Leave blank to use the default of {DEFAULT_MONTHLY_LIMIT}. Takes effect right away.
        </span>
        {!validNumber && (
          <p className="vb-users-error" role="alert">
            Enter a whole number greater than zero, or leave it blank for the default.
          </p>
        )}
      </div>
      <div className="vb-users-field" style={{ marginTop: 10 }}>
        <label htmlFor="vb-limit-reason">Reason (kept in the history)</label>
        <input
          id="vb-limit-reason"
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Listing season, busy month"
        />
      </div>
      {/* THE AFTER-SAVE PREVIEW. No "used" figure is shown here — this screen is not handed this
          account's count for OTHER accounts, only its own limit, so the preview states the limit
          that will take effect rather than inventing a usage number this component was never
          given (never a figure with no source, per the owner's strict-tier rule). */}
      <p
        style={{
          marginTop: 10,
          fontSize: 13,
          color: "var(--vb-color-text, #1E293B)",
          background: "var(--vb-color-accent-soft, #EFF4FF)",
          borderRadius: 6,
          padding: "8px 10px",
        }}
      >
        After saving: {previewLimit} report{previewLimit === 1 ? "" : "s"} per month
        {parsed === null ? " (the default)" : ""}.
      </p>
      {error && (
        <p className="vb-users-error" role="alert">
          {error}
        </p>
      )}
    </Dialog>
  );
}

function ResetLinkDialog({
  user: person,
  url,
  expires,
  onClose,
}: {
  user: PlatformUser;
  url: string;
  expires: string | null;
  onClose: () => void;
}) {
  const [copied, setCopied] = useState(false);
  return (
    <Dialog
      title="Their one-time reset link"
      sub={`Copy it now and give it to ${person.name}. It will not be shown again.`}
      onClose={onClose}
      footer={
        <button className="vb-users-btn" data-variant="primary" type="button" onClick={onClose}>
          I have copied it
        </button>
      }
    >
      <input
        className="vb-users-secret"
        readOnly
        value={url}
        aria-label="One-time reset link"
        onFocus={(e) => e.currentTarget.select()}
      />
      <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
        <button
          className="vb-users-btn"
          type="button"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText(url);
              setCopied(true);
            } catch {
              // Clipboard access can be refused (an insecure origin, a permission prompt the user
              // dismissed). The link is on screen and selectable, so say so rather than failing.
              setCopied(false);
            }
          }}
        >
          Copy link
        </button>
        {copied && <span style={{ fontSize: 12 }} role="status">Copied.</span>}
      </div>
      <p className="vb-users-hint" style={{ marginTop: 0 }}>
        It works once and expires {expires ? `on ${formatDay(expires)}` : "in 24 hours"}. Opening it
        lets {person.name} choose their own password — you will not know it. This link is not stored
        anywhere you can read it again; if it is lost, issue another from their row.
      </p>
    </Dialog>
  );
}

export default UsersScreen;
